import { useCallback, useEffect, useRef, useState } from 'react';

export type RecorderStatus =
  | 'idle' | 'requesting' | 'ready' | 'recording' | 'recorded'
  | 'denied' | 'unsupported' | 'error';

export interface Recorder {
  status: RecorderStatus;
  stream: MediaStream | null;
  videoBlob: Blob | null;
  videoUrl: string | null;
  elapsedMs: number;
  error: string | null;
  requestCamera(): Promise<void>;
  start(): void;
  stop(): void;
  retake(): void;
  reset(): void;
}

const MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
];
const TICK_MS = 200;

function isSupported(): boolean {
  return typeof MediaRecorder !== 'undefined'
    && typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia;
}

function pickMimeType(): string | undefined {
  for (const t of MIME_CANDIDATES) {
    try { if (MediaRecorder.isTypeSupported(t)) return t; } catch { /* ignore */ }
  }
  return undefined;
}

export function useRecorder(): Recorder {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const urlRef = useRef<string | null>(null);
  // Intentionally NOT cleared by reset(): the reentrancy guard must survive a
  // StrictMode dev remount (mount→cleanup→mount) so the phase-2 requestCamera is
  // a no-op and we don't spawn a second getUserMedia (which would resurrect the
  // concurrent-request stream leak this guard exists to prevent).
  const requestingRef = useRef(false);
  const disposedRef = useRef(false);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  const revokeUrl = () => { if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; } };
  const clearTake = () => { revokeUrl(); setVideoUrl(null); setVideoBlob(null); setElapsedMs(0); };

  const requestCamera = useCallback(async () => {
    if (!isSupported()) { setStatus('unsupported'); return; }
    // Reentrancy guard: a second call while the first is in flight would leave the
    // loser's live stream tracks running forever (invisible camera-track leak).
    if (requestingRef.current) return;
    requestingRef.current = true;
    setStatus('requesting'); setError(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
      // If the component unmounted while getUserMedia was in flight, don't adopt
      // the now-live stream into a dead hook instance — stop it immediately, or
      // its tracks (and the camera indicator) would run forever.
      if (disposedRef.current) { s.getTracks().forEach((t) => t.stop()); return; }
      // Defensive: stop any previously-adopted stream before replacing it.
      if (streamRef.current && streamRef.current !== s) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      streamRef.current = s;
      setStream(s);
      setStatus('ready');
    } catch (e) {
      const name = (e as { name?: string })?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
        setStatus('denied');
      } else {
        setError(e instanceof Error ? e.message : 'Your camera is unavailable.');
        setStatus('error');
      }
    } finally {
      requestingRef.current = false;
    }
  }, []);

  const start = useCallback(() => {
    const s = streamRef.current;
    if (!s) return;
    // Guard against a double start(): a second start while already recording would
    // overwrite timerRef (leaking the first interval) and spin up a second recorder
    // pushing into the same shared chunks buffer (corrupting the blob).
    if (recorderRef.current && recorderRef.current.state !== 'inactive') return;
    stopTimer();
    clearTake();
    chunksRef.current = [];
    const mimeType = pickMimeType();
    const rec = new MediaRecorder(s, mimeType ? { mimeType } : undefined);
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
    rec.onstop = () => {
      const type = rec.mimeType || mimeType || 'video/webm';
      const blob = new Blob(chunksRef.current, { type });
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setVideoBlob(blob);
      setVideoUrl(url);
      setStatus('recorded');
      stopTimer();
    };
    rec.onerror = () => { setError('Something interrupted the recording.'); setStatus('error'); stopTimer(); };
    recorderRef.current = rec;
    rec.start();
    setStatus('recording');
    timerRef.current = setInterval(() => setElapsedMs((ms) => ms + TICK_MS), TICK_MS);
  }, []);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    stopTimer();
  }, []);

  const retake = useCallback(() => {
    clearTake();
    chunksRef.current = [];
    setStatus(streamRef.current ? 'ready' : 'idle');
  }, []);

  const reset = useCallback(() => {
    stopTimer();
    const rec = recorderRef.current;
    if (rec) {
      // Detach handlers FIRST. In a real browser MediaRecorder.stop() is async, so
      // the queued onstop would otherwise fire after we've torn down — calling
      // URL.createObjectURL (leaking a URL nobody revokes) and setState after unmount.
      rec.ondataavailable = null;
      rec.onstop = null;
      rec.onerror = null;
      if (rec.state !== 'inactive') { try { rec.stop(); } catch { /* ignore */ } }
    }
    recorderRef.current = null;
    const s = streamRef.current;
    if (s) s.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
    clearTake();
    setStatus('idle');
  }, []);

  // Turn the camera light off when the component using this hook unmounts.
  // Reset the disposed flag on (re)mount so React StrictMode's dev
  // mount→cleanup→mount cycle doesn't leave it stuck true — otherwise the
  // post-await guard in requestCamera would stop the stream and never reach
  // 'ready'. On a real unmount the flag stays true and the leak guard fires.
  useEffect(() => {
    disposedRef.current = false;
    return () => { disposedRef.current = true; reset(); };
  }, [reset]);

  return { status, stream, videoBlob, videoUrl, elapsedMs, error, requestCamera, start, stop, retake, reset };
}
